<template>
  <d-modal @close="handleClose">
    <d-modal-header>
      <d-modal-title>
        {{ $t('customer.editAddress') }}
      </d-modal-title>
    </d-modal-header>
    <d-modal-body>
      <d-form>
        <validation-observer ref="customer-address-edit-form" tag="form">
          <div class="form-group">
            <label>
              {{ $t('settings.country.country') }}:
            </label>

            <validation-provider
              rules="required"
              name="country-id"
              v-slot="{ errors }">
              <d-form-select
                v-model="country_id"
                :state="errors.length ? 'invalid' : null"
                :options="countriesOptions" />
              <div class="invalid-feedback">
                {{ errors[0] }}
              </div>
            </validation-provider>
          </div>

          <div class="form-group">
            <label>
              {{ $t('ui.nameAndSurename') }}:
            </label>

            <validation-provider
              rules="required"
              name="name"
              v-slot="{ errors }">
              <d-input
                :state="errors.length ? 'invalid' : null"
                v-model="name" />
              <div class="invalid-feedback">
                {{ errors[0] }}
              </div>
            </validation-provider>
          </div>

          <div class="form-group">
            <label>
              {{ $t('ui.emailAddress') }}:
            </label>

            <validation-provider
              rules="email"
              name="email"
              v-slot="{ errors }">
              <d-input
                :state="errors.length ? 'invalid' : null"
                v-model="email" />
              <div class="invalid-feedback">
                {{ errors[0] }}
              </div>
            </validation-provider>
          </div>

          <div class="form-group">
            <label>
              {{ $t('customer.companyName') }}:
            </label>

            <validation-provider
              rules="required"
              name="company-name"
              v-slot="{ errors }">
              <d-input
                :state="errors.length ? 'invalid' : null"
                v-model="company_name" />
              <div class="invalid-feedback">
                {{ errors[0] }}
              </div>
            </validation-provider>
          </div>

          <div class="form-group">
            <label for="name">
              {{ $t('customer.street') }}:
            </label>

            <validation-provider
              rules="required"
              name="street"
              v-slot="{ errors }">
              <d-input
                :state="errors.length ? 'invalid' : null"
                v-model="street" />
              <div class="invalid-feedback">
                {{ errors[0] }}
              </div>
            </validation-provider>
          </div>

          <div class="d-flex">
            <div class="form-group w-100">
              <label>
                {{ $t('customer.building') }}:
              </label>
              <d-input v-model="building" />
            </div>

            <div class="form-group w-100 ml-2">
              <label>
                {{ $t('customer.houseNumber') }}:
              </label>

              <d-input v-model="locale" />
            </div>
          </div>

          <div class="form-group">
            <label>
              {{ $t('customer.addressLine2') }}:
            </label>

            <validation-provider
              rules="max:35"
              name="line_2"
              v-slot="{ errors }">
              <d-input
                :state="errors.length ? 'invalid' : null"
                v-model="line_2" />
              <div class="invalid-feedback">
                {{ errors[0] }}
              </div>
            </validation-provider>
          </div>

          <div class="form-group">
            <label>
              {{ $t('customer.addressLine3') }}:
            </label>

            <validation-provider
              rules="max:35"
              name="line_3"
              v-slot="{ errors }">
              <d-input
                :state="errors.length ? 'invalid' : null"
                v-model="line_3" />
              <div class="invalid-feedback">
                {{ errors[0] }}
              </div>
            </validation-provider>
          </div>

          <div class="d-flex">
            <div class="form-group w-100">
              <label>
                {{ $t('customer.zipCode') }}:
              </label>

              <validation-provider
                :rules="country_id === polandCountryID ? 'required|postal_code_pl' : 'required'"
                name="postcode"
                v-slot="{ errors }">
                <d-input
                  v-if="country_id === polandCountryID"
                  key="invoice-postcode-mask"
                  v-mask="'##-###'"
                  :state="errors.length ? 'invalid' : null"
                  v-model="postcode" />
                <d-input
                  v-else
                  key="invoice-postcode-nomask"
                  :state="errors.length ? 'invalid' : null"
                  v-model="postcode" />
                <div class="invalid-feedback">
                  {{ errors[0] }}
                </div>
              </validation-provider>
            </div>

            <div class="form-group w-100 ml-2">
              <label>
                {{ $t('customer.city') }}:
              </label>

              <validation-provider
                rules="required"
                name="city"
                v-slot="{ errors }">
                <d-input
                  :state="errors.length ? 'invalid' : null"
                  v-model="city" />
                <div class="invalid-feedback">
                  {{ errors[0] }}
                </div>
              </validation-provider>
            </div>
          </div>

          <div class="form-group">
            <label>
              {{ $t('customer.telephone') }}:
            </label>

            <validation-provider
              rules="required"
              name="phone"
              v-slot="{ errors }">
              <d-input
                v-mask="'+## ### ### ### ### ### ### ### ### ###'"
                :state="errors.length ? 'invalid' : null"
                v-model="phone" />
              <div class="invalid-feedback">
                {{ errors[0] }}
              </div>
            </validation-provider>
          </div>
        </validation-observer>
      </d-form>
    </d-modal-body>
    <d-modal-footer>
      <d-button
        class="ml-0"
        theme="danger"
        @click.prevent="cancel">
        {{ $t('ui.cancel') }}
      </d-button>
      <d-button
        class="ml-auto"
        theme="success"
        @click.prevent="validateForm">
        {{ $t('ui.saveChanges') }}
      </d-button>
    </d-modal-footer>
  </d-modal>
</template>

<script>
import CommonGetters from './../../utils/CommonGetters.js';
import FormUtils from './../../utils/FormUtils.js';

export default {
  name: 'customer-address-edit-popup',
  props: {
    customerID: {
      default: 0,
      type: Number
    },
    addressData: {
      default: () => ({}),
      type: Object
    },
    countries: {
      default: () => ([]),
      type: Array
    }
  },
  computed: {
    countriesOptions () {
      return CommonGetters.getCountriesForSelect(this.countries);
    },
    polandCountryID () {
      let countryPL = this.countries.filter(country => country.code === 'PL');

      if (countryPL[0]) {
        return countryPL[0].id;
      }

      return false;
    }
  },
  data () {
    return {
      name: '',
      email: '',
      company_name: '',
      country_id: 0,
      street: '',
      building: '',
      locale: '',
      line_2: '',
      line_3: '',
      postcode: '',
      city: '',
      phone: ''
    };
  },
  mounted () {
    this.id = this.addressData.id;
    this.name = this.addressData.name;
    this.email = this.addressData.email;
    this.company_name = this.addressData.company_name;
    this.country_id = this.addressData.country_id;
    this.street = this.addressData.street;
    this.building = this.addressData.building;
    this.locale = this.addressData.locale;
    this.line_2 = this.addressData.line_2;
    this.line_3 = this.addressData.line_3;
    this.postcode = this.addressData.postcode;
    this.city = this.addressData.city;
    this.phone = this.addressData.phone;
  },
  methods: {
    handleClose () {
      this.$bus.$emit('customer-edit-address-popup-close');
    },
    validateForm () {
      FormUtils.validate(this.$refs['customer-address-edit-form'], this.saveCustomerAddress);
    },
    saveCustomerAddress () {
      FormUtils.submit({
        $http: this.$http,
        endpoint: '/customers/addresses/update/' + this.id,
        formData: {
          customer_id: this.customerID,
          name: (this.name).toString().toLocaleUpperCase(),
          email: (this.email).toString().toLocaleUpperCase(),
          company_name: (this.company_name).toString().toLocaleUpperCase(),
          country_id: this.country_id,
          street: (this.street).toString().toLocaleUpperCase(),
          building: (this.building).toString().toLocaleUpperCase(),
          locale: (this.locale).toString().toLocaleUpperCase(),
          line_2: (this.line_2).toString().toLocaleUpperCase(),
          line_3: (this.line_3).toString().toLocaleUpperCase(),
          postcode: (this.postcode).toString().toLocaleUpperCase(),
          city: (this.city).toString().toLocaleUpperCase(),
          phone: this.phone
        },
        successTitle: this.$t('customer.editAddressSuccessTitle'),
        successText: this.$t('customer.editAddressSuccessText'),
        errorTitle: this.$t('ui.errorOccured'),
        errorText: this.$t('customer.editAddressErrorText'),
        successAction: () => {
          this.$bus.$emit('customer-reload-addresses');
          this.$bus.$emit('order-select-address-fetch-data');
          this.handleClose();
        }
      });
    },
    cancel () {
      this.handleClose();
    }
  }
}
</script>
